// Saved-filter shape helpers.
//
// A saved filter reaches the grid from three places that disagree about casing
// and field names:
//
//   1. localStorage  -- created here before migrate-070: { id, name, staffIds }
//   2. the API       -- PascalCase columns + lowercase parsed arrays
//   3. myHomeroomFilter -- generated on the fly, never persisted
//
// That is why read sites used to be written `f.name || f.Name`. Everything is
// normalized on the way in instead, so the rest of the grid sees one shape.

export const FILTER_KINDS = ['staff', 'client'];
export const FILTER_VISIBILITIES = ['private', 'shared', 'global'];

/**
 * Collapse any saved-filter representation into the canonical shape.
 *
 * `memberIds` replaces the old `staffIds` because a filter is no longer
 * necessarily about people -- for kind 'client' these are ClientIds. The
 * `staffIds` fallback is what keeps pre-migrate-070 cache entries readable.
 */
export function normalizeSavedFilter(f) {
  if (!f || typeof f !== 'object') return null;
  return {
    id: f.FilterId ?? f.id,
    // Only set when the row exists server-side. Locally-created rows carry a
    // string id ('f' + timestamp) until the save round-trips, and passing one of
    // those to delete_filter would just 400.
    filterId: f.FilterId ?? (typeof f.id === 'number' ? f.id : null),
    name: f.Name ?? f.name ?? '',
    kind: f.Kind ?? f.kind ?? 'staff',
    memberIds: f.memberIds ?? f.staffIds ?? [],
    visibility: f.Visibility ?? f.visibility ?? 'private',
    sharedWith: f.sharedWith ?? [],
    ownerEmail: f.UserEmail ?? f.ownerEmail ?? null,
    ownerName: f.OwnerName ?? f.ownerName ?? null,
    // Anything with no explicit flag was created locally, so it is mine. Only
    // the API says otherwise, and only for shared/global rows. Defaulting the
    // other way would hide the delete control on every filter a user just made.
    isMine: f.isMine ?? true,
    isSystem: f.isSystem ?? false,
    createdAt: f.CreatedAt ?? f.createdAt ?? null,
    updatedAt: f.UpdatedAt ?? f.updatedAt ?? null,
  };
}

/**
 * Identity of a filter within one person's list.
 *
 * Kind is part of the key because the DB's unique index is
 * (UserEmail, Kind, Name): "Construction Team" can exist as both a set of
 * people and a set of clients, and keying on name alone would let one silently
 * replace the other.
 */
export function savedFilterKey(f) {
  return `${f?.kind || 'staff'}::${f?.name ?? ''}`;
}

/**
 * What gets written to localStorage.
 *
 * Only the user's own filters. Shared and global filters belong to someone else
 * and can be edited or revoked at any time, so caching them would show a stale
 * copy — potentially after access was withdrawn. They are re-fetched each load.
 * The generated homeroom filter is likewise derived, never stored.
 */
export function cacheableFilters(list) {
  return (list || []).filter(f => f && f.isMine && !f.isSystem);
}
